import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useImageViewport } from './useImageViewport';

import { generateGetSignedUrl, listS3Objects } from 'api/action';
import * as Sentry from '@sentry/react';
import type { Parser } from 'papaparse';

import {
  type AttachmentPreviewType,
  type CsvPreviewData,
  type CsvPreviewError,
  type ExcelPreviewData,
  type ExcelPreviewError,
  getAttachmentPreviewType,
  loadCsvPreview,
  loadExcelPreview,
} from './uploadAttachmentPreview';

const INTAKE_BUCKET = `intake-items-${import.meta.env.VITE_ACCOUNT_ID}`;
const ATTACHMENT_FILE_ERROR = 'Failed to load attachment.';
const CSV_PREVIEW_FILE_ERROR = 'Failed to load CSV preview.';
const CSV_PREVIEW_PARSE_ERROR = 'Failed to parse CSV preview.';
const EXCEL_PREVIEW_FILE_ERROR = 'Failed to load Excel preview.';
const EXCEL_PREVIEW_PARSE_ERROR = 'Failed to parse Excel preview.';

type AttachmentPreviewLoadMode =
  | 'preview'
  | 'csv-load'
  | 'csv-parse'
  | 'excel-load'
  | 'excel-parse';

const getFileNameFromKey = (key: string) => {
  const decoded = decodeURIComponent(key);
  const pieces = decoded.split('/');
  const fileName = pieces[pieces.length - 1];
  const splitFileName = fileName.split('__');
  return splitFileName[1] || fileName || 'attachment';
};

const filterAttachments = (key: string, prefix: string) => {
  if (!key || key.endsWith('/')) {
    return false;
  }

  const normalizedPrefix = prefix.endsWith('/') ? prefix : `${prefix}/`;
  const relativeKey = key.startsWith(normalizedPrefix)
    ? key.slice(normalizedPrefix.length)
    : key;

  return relativeKey !== 'results' && !relativeKey.startsWith('results/');
};

const listAttachmentKeys = async (prefix: string): Promise<string[]> => {
  const keys = await listS3Objects({
    bucket: INTAKE_BUCKET,
    prefix: encodeURIComponent(prefix),
  });

  return (keys || [])
    .filter((key) => filterAttachments(key, prefix))
    .sort((a, b) => a.localeCompare(b));
};

export type AttachmentDownloadError = 'single' | 'multiple';

interface UseAttachmentViewerParams {
  modalOpen: boolean;
  prefix: string;
}

export interface AttachmentViewerState {
  fileIndex: number;
  fileKeys: string[];
  fileLoading: boolean;
  fileError: string | null;
  fileUrl: string;
  listLoading: boolean;
  listError: string | null;
  zoom: number;
  csvPreview: CsvPreviewData | null;
  excelPreview: ExcelPreviewData | null;
  previewType: AttachmentPreviewType;
  canPrev: boolean;
  canNext: boolean;
  goPrev: () => void;
  goNext: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  downloadCurrent: () => Promise<void>;
  downloadAll: () => Promise<void>;
  downloadCurrentLoading: boolean;
  downloadAllLoading: boolean;
  downloadError: AttachmentDownloadError | null;
  handleImageDoubleClick: (event: ReactMouseEvent<HTMLImageElement>) => void;
  handleImageViewportRef: (node: HTMLDivElement | null) => void;
  handleImageLoad: (naturalWidth: number, naturalHeight: number) => void;
}

export const useUploadAttachmentViewer = ({
  modalOpen,
  prefix,
}: UseAttachmentViewerParams): AttachmentViewerState => {
  const [listLoading, setListLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileKeys, setFileKeys] = useState<string[]>([]);
  const [fileUrl, setFileUrl] = useState('');
  const [fileIndex, setFileIndex] = useState(0);
  const [listError, setListError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvPreviewData | null>(null);
  const [excelPreview, setExcelPreview] = useState<ExcelPreviewData | null>(
    null
  );
  const [downloadCurrentLoading, setDownloadCurrentLoading] = useState(false);
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);
  const [downloadError, setDownloadError] =
    useState<AttachmentDownloadError | null>(null);
  const csvPreviewParserRef = useRef<Parser | null>(null);

  const activeKey = fileKeys[fileIndex] || '';
  const previewType = getAttachmentPreviewType(activeKey);
  const isImagePreview = previewType === 'image';
  const viewport = useImageViewport(activeKey, isImagePreview);
  const { resetZoom } = viewport;
  const isCsvPreview = previewType === 'csv';
  const isExcelPreview = previewType === 'excel';

  const clearTabularPreviews = useCallback(() => {
    setCsvPreview(null);
    setExcelPreview(null);
  }, []);

  const getSignedUrlForKey = useCallback(async (key: string) => {
    return generateGetSignedUrl({
      bucket: INTAKE_BUCKET,
      key: encodeURIComponent(key),
    });
  }, []);

  const downloadKeyFromSignedUrl = useCallback(
    async (key: string, signedUrl: string) => {
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error(`Failed to download attachment for key: ${key}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = getFileNameFromKey(key);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    },
    []
  );

  useEffect(() => {
    if (!modalOpen || !prefix) {
      setListLoading(false);
      setListError(null);
      setFileKeys([]);
      setFileIndex(0);
      return;
    }

    let cancelled = false;
    setListLoading(true);
    setListError(null);

    listAttachmentKeys(prefix)
      .then((keys) => {
        if (cancelled) return;
        setFileKeys(keys);
        setFileIndex(0);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setListError('Failed to load intake attachments.');
        Sentry.captureException(error, {
          tags: { component: 'useUploadAttachmentViewer' },
          extra: { bucket: INTAKE_BUCKET, prefix },
        });
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [modalOpen, prefix]);

  useEffect(() => {
    if (!modalOpen) {
      setFileKeys([]);
      setFileIndex(0);
      setFileUrl('');
      setListError(null);
      setFileError(null);
      clearTabularPreviews();
      setDownloadError(null);
      resetZoom();
    }
  }, [clearTabularPreviews, modalOpen, resetZoom]);

  useEffect(() => {
    if (!modalOpen || !activeKey) {
      setFileUrl('');
      clearTabularPreviews();
      return;
    }

    let cancelled = false;

    const setPreviewErrorForMode = (previewMode: AttachmentPreviewLoadMode) => {
      if (previewMode === 'csv-parse') {
        setFileError(CSV_PREVIEW_PARSE_ERROR);
        return;
      }
      if (previewMode === 'csv-load') {
        setFileError(CSV_PREVIEW_FILE_ERROR);
        return;
      }
      if (previewMode === 'excel-parse') {
        setFileError(EXCEL_PREVIEW_PARSE_ERROR);
        return;
      }
      if (previewMode === 'excel-load') {
        setFileError(EXCEL_PREVIEW_FILE_ERROR);
        return;
      }
      setFileError(ATTACHMENT_FILE_ERROR);
    };

    const capturePreviewError = (
      error: unknown,
      previewMode: AttachmentPreviewLoadMode
    ) => {
      Sentry.captureException(error, {
        tags: { component: 'UploadViewerModal' },
        extra: {
          bucket: INTAKE_BUCKET,
          key: activeKey,
          mode: previewMode,
        },
      });
    };

    const loadCsvAttachmentPreview = async (signedUrl: string) => {
      try {
        const nextCsvPreview = await loadCsvPreview(signedUrl, (parser) => {
          csvPreviewParserRef.current = parser;
        });
        if (cancelled) return;
        setCsvPreview(nextCsvPreview);
      } catch (error) {
        if (cancelled) return;
        const previewMode =
          (error as CsvPreviewError).previewMode || 'csv-load';
        setPreviewErrorForMode(previewMode);
        capturePreviewError(error, previewMode);
      }
    };

    const loadExcelAttachmentPreview = async (signedUrl: string) => {
      try {
        const nextExcelPreview = await loadExcelPreview(signedUrl);
        if (cancelled) return;
        setExcelPreview(nextExcelPreview);
      } catch (error) {
        if (cancelled) return;
        const previewMode =
          (error as ExcelPreviewError).previewMode || 'excel-load';
        setPreviewErrorForMode(previewMode);
        capturePreviewError(error, previewMode);
      }
    };

    const fetchPreview = async () => {
      setFileLoading(true);
      setFileError(null);
      clearTabularPreviews();

      try {
        const signed = await generateGetSignedUrl({
          bucket: INTAKE_BUCKET,
          key: encodeURIComponent(activeKey),
        });
        if (cancelled) return;
        setFileUrl(signed);

        if (isCsvPreview) {
          await loadCsvAttachmentPreview(signed);
          return;
        }

        if (isExcelPreview) {
          await loadExcelAttachmentPreview(signed);
        }
      } catch (error) {
        if (cancelled) return;
        const previewMode: AttachmentPreviewLoadMode = isCsvPreview
          ? 'csv-load'
          : isExcelPreview
          ? 'excel-load'
          : 'preview';
        setPreviewErrorForMode(previewMode);
        capturePreviewError(error, previewMode);
      } finally {
        if (!cancelled) {
          setFileLoading(false);
        }
        csvPreviewParserRef.current = null;
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
      csvPreviewParserRef.current?.abort();
      csvPreviewParserRef.current = null;
    };
  }, [
    activeKey,
    clearTabularPreviews,
    isCsvPreview,
    isExcelPreview,
    modalOpen,
  ]);

  useEffect(() => setDownloadError(null), [activeKey]);

  const canNavigate = fileKeys.length > 1;
  const canPrev = canNavigate;
  const canNext = canNavigate;

  const downloadCurrent = useCallback(async () => {
    if (!activeKey) return;
    setDownloadCurrentLoading(true);
    setDownloadError(null);
    try {
      // Always request a fresh signed URL for download to avoid stale/preview URL issues.
      const signedUrl = await getSignedUrlForKey(activeKey);
      await downloadKeyFromSignedUrl(activeKey, signedUrl);
    } catch (error) {
      setDownloadError('single');
      Sentry.captureException(error, {
        tags: { component: 'useUploadAttachmentViewer' },
        extra: { bucket: INTAKE_BUCKET, key: activeKey, mode: 'single' },
      });
    } finally {
      setDownloadCurrentLoading(false);
    }
  }, [activeKey, downloadKeyFromSignedUrl, getSignedUrlForKey]);

  const downloadAll = useCallback(async () => {
    if (!fileKeys.length) return;
    setDownloadAllLoading(true);
    setDownloadError(null);

    let failedDownloads = 0;

    for (const key of fileKeys) {
      try {
        const signedUrl = await getSignedUrlForKey(key);
        await downloadKeyFromSignedUrl(key, signedUrl);
      } catch (error) {
        failedDownloads += 1;
        Sentry.captureException(error, {
          tags: { component: 'useUploadAttachmentViewer' },
          extra: { bucket: INTAKE_BUCKET, key, mode: 'all' },
        });
      }
    }

    if (failedDownloads > 0) {
      setDownloadError('multiple');
    }

    setDownloadAllLoading(false);
  }, [downloadKeyFromSignedUrl, fileKeys, getSignedUrlForKey]);

  return {
    fileIndex,
    fileKeys,
    fileLoading,
    fileError,
    fileUrl,
    listLoading,
    listError,
    ...viewport,
    csvPreview,
    excelPreview,
    previewType,
    canPrev,
    canNext,
    goPrev: () => {
      if (!fileKeys.length) return;
      setFileIndex((prev) => (prev - 1 + fileKeys.length) % fileKeys.length);
    },
    goNext: () => {
      if (!fileKeys.length) return;
      setFileIndex((prev) => (prev + 1) % fileKeys.length);
    },
    downloadCurrent,
    downloadAll,
    downloadCurrentLoading,
    downloadAllLoading,
    downloadError,
  };
};
